/**
 * A view's assets (docs/views.md#2-data): files an agent or a page uploads, such as a dish's photo or a logo, kept by
 * their SHA-256 and served from the view's own address, `/api/views/<view>/assets/<hash>.<ext>`, so a record holds the
 * URL rather than the file. Coast cloud keeps them in R2 and the self-host on disk. Images only: an SVG could run script
 * on the server's origin when opened by itself, so it isn't taken.
 */
export const ASSET_LIMITS = {
  /** One asset's bytes at most. */
  bytes: 5 << 20,
  /** A view's assets' bytes at most, all together: a repository's published view, such as Design, keeps its pictures here. */
  viewBytes: 200 << 20,
  /** How long fetching an asset from a URL may take, its redirects included. */
  fetchMs: 10_000,
  /** Redirects a fetch follows, at most. */
  redirects: 3,
} as const;

/** The types an asset may have, by the extension its URL ends in. */
export const ASSET_TYPES = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
} as const;
export type AssetType = keyof typeof ASSET_TYPES;
export const isAssetType = (value: unknown): value is AssetType => typeof value === "string" && value in ASSET_TYPES;

/** The path a view's asset is served at, on the view's server; with no file, the folder its page may load images from. */
export const assetPath = (view: string, file: string) => `/api/views/${view}/assets/${file}`;
/** Where every view's assets are served, which any view's page may load images from (src/client/renderer.ts). */
export const ASSETS_PATH = "/api/views/";

/** An asset's file name in its URL: its SHA-256 in hex and its type's extension. */
export const ASSET_FILE = /^([a-f0-9]{64})\.(png|jpg|webp|gif|avif)$/;
export const assetFile = (hash: string, type: AssetType) => `${hash}.${ASSET_TYPES[type]}`;
export const typeOfFile = (file: string): AssetType | null => {
  const extension = ASSET_FILE.exec(file)?.[2];
  return (Object.entries(ASSET_TYPES).find(([, ext]) => ext === extension)?.[0] as AssetType | undefined) ?? null;
};

/**
 * Whether `url` is https on a host named on the internet: not an IP literal, `localhost`, or a local name such as
 * `printer.local`. The server fetches assets, and the self-host's render browser loads pages' requests, from the
 * server's own machine, which must not reach itself or its network for whoever asks.
 */
export function onTheInternet(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
  const literal = /^[\d.]+$/.test(host) || host.startsWith("[");
  return !literal && host.includes(".") && !/(^|\.)(localhost|local|internal|lan|home|arpa)$/.test(host);
}

/** The type the bytes themselves say, from their first bytes, whatever a caller claims. */
export function sniffAssetType(bytes: Uint8Array): AssetType | null {
  const starts = (...values: number[]) => values.every((value, index) => bytes[index] === value);
  const text = (offset: number, value: string) =>
    [...value].every((character, index) => bytes[offset + index] === character.charCodeAt(0));
  if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (starts(0xff, 0xd8, 0xff)) return "image/jpeg";
  if (text(0, "RIFF") && text(8, "WEBP")) return "image/webp";
  if (text(0, "GIF87a") || text(0, "GIF89a")) return "image/gif";
  if (text(4, "ftypavif") || text(4, "ftypavis")) return "image/avif";
  return null;
}
